import {
  AccountInheritedLicenses,
  AccountLicense,
  AccountSubscription,
  AccountSubscriptions
} from '@/domain/protocols/AccountSubscriptions'

import { DateResponseMapper } from './authentication'

/** Licença como vem no wire de `GET /v2/users/me/subscriptions`. */
export interface AccountLicenseRemote {
  id: number
  reason: string
  startAt: string
  endAt: string
}

export interface AccountSubscriptionRemote {
  id: number
  status: string
  offerCode?: string
  offerDisplayName?: string
  productGroupName?: string
  method?: string
  cardLastDigits?: string
  latestBoletoDueAt?: string
  createdAt: string
  licenses?: AccountLicenseRemote[]
}

export interface AccountInheritedLicensesRemote {
  manager: {
    id: number
    email: string
    name?: string
    surname?: string
    nickname?: string
  }
  licenses?: AccountLicenseRemote[]
}

/** Resposta de `GET /v2/users/me/subscriptions`. O wire já vem em camelCase. */
export interface AccountSubscriptionsRemote {
  subscriptions?: AccountSubscriptionRemote[]
  licenses?: AccountLicenseRemote[]
  inherited?: AccountInheritedLicensesRemote | null
}

const LicensesResponseMapper = (licenses: AccountLicenseRemote[]): AccountLicense[] =>
  (Array.isArray(licenses) ? licenses : []).map((license) => ({
    id: license.id,
    reason: license.reason ?? null,
    startAt: DateResponseMapper(license.startAt),
    endAt: DateResponseMapper(license.endAt)
  }))

const SubscriptionResponseMapper = (subscription: AccountSubscriptionRemote): AccountSubscription => ({
  id: subscription.id,
  status: subscription.status ?? null,
  offerDisplayName: subscription.offerDisplayName || null,
  productGroupName: subscription.productGroupName || null,
  method: subscription.method ?? null,
  cardLastDigits: subscription.cardLastDigits || null,
  latestBoletoDueAt: DateResponseMapper(subscription.latestBoletoDueAt),
  createdAt: DateResponseMapper(subscription.createdAt),
  licenses: LicensesResponseMapper(subscription.licenses)
})

const InheritedResponseMapper = (inherited: AccountInheritedLicensesRemote): AccountInheritedLicenses => {
  if (!inherited?.manager) return null
  return {
    manager: {
      id: inherited.manager.id,
      email: inherited.manager.email,
      name: inherited.manager.name || null,
      surname: inherited.manager.surname || null,
      nickname: inherited.manager.nickname || null
    },
    licenses: LicensesResponseMapper(inherited.licenses)
  }
}

/** Devolve `null` quando a resposta não é um objeto (resposta inválida). */
export const AccountSubscriptionsResponseMapper = (response: AccountSubscriptionsRemote): AccountSubscriptions => {
  if (!response || typeof response !== 'object') return null
  return {
    subscriptions: (Array.isArray(response.subscriptions) ? response.subscriptions : []).map(
      SubscriptionResponseMapper
    ),
    licenses: LicensesResponseMapper(response.licenses),
    inherited: InheritedResponseMapper(response.inherited)
  }
}
